// contexts/CompanyAuthContext.tsx - Fixed state management
import { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { demoCompanyService } from '@/lib/demoCompanyService';

interface CompanyUser {
  id: string;
  name: string;
  email: string;
  companyName: string;
  contactName: string;
  contactTitle: string;
  phone: string;
  location: string;
  industry: string;
  isDemo?: boolean;
}

interface CompanyAuthContextType {
  user: CompanyUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  getRememberedEmail: () => string | null;
  saveRememberedEmail: (email: string) => void;
  clearRememberedEmail: () => void;
}

const CompanyAuthContext = createContext<CompanyAuthContextType | undefined>(undefined);
const REMEMBERED_EMAIL_KEY = 'company_remembered_email';

export function CompanyAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CompanyUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Load session from localStorage (both demo and real)
    const savedUser = localStorage.getItem('company_session');
    const sessionUser = sessionStorage.getItem('company_session');
    const activeUser = savedUser || sessionUser;
    
    if (activeUser) {
      try {
        const parsedUser = JSON.parse(activeUser);
        console.log('Loaded saved user:', parsedUser);
        setUser(parsedUser);
      } catch (e) {
        console.error('Failed to load company session', e);
        // Clear invalid data
        localStorage.removeItem('company_session');
        sessionStorage.removeItem('company_session');
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string, rememberMe?: boolean): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log('Attempting login for:', email);
      
      // STEP 1: Try demo accounts first
      const demoCompanies = demoCompanyService.getAllCompanies();
      const demoCompany = demoCompanies.find(c => c.email === email);
      
      if (demoCompany && password === 'demo123') {
        console.log('Demo login successful for:', demoCompany.name);
        
        // Demo account login successful
        const companyUser: CompanyUser = {
          id: demoCompany.id,
          name: demoCompany.contactName,
          email: demoCompany.email,
          companyName: demoCompany.name,
          contactName: demoCompany.contactName,
          contactTitle: demoCompany.contactTitle,
          phone: demoCompany.phone,
          location: demoCompany.location,
          industry: demoCompany.industry,
          isDemo: true,
        };
        
        // Set user state FIRST
        setUser(companyUser);
        
        // Then store in storage
        if (rememberMe) {
          localStorage.setItem('company_session', JSON.stringify(companyUser));
          console.log('Saved to localStorage');
        } else {
          sessionStorage.setItem('company_session', JSON.stringify(companyUser));
          console.log('Saved to sessionStorage');
        }
        
        // Small delay to ensure state is updated
        await new Promise(resolve => setTimeout(resolve, 100));
        
        return { success: true };
      }
      
      // STEP 2: Try real accounts from Supabase
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('email', email)
        .single();

      if (error || !data) {
        console.log('No real account found:', error);
        return { success: false, error: 'Invalid email or password' };
      }
      
      // Check password (plain text for now - you should hash these)
      if (data.password !== password) {
        return { success: false, error: 'Invalid email or password' };
      }
      
      console.log('Real account login successful for:', data.name);
      
      // Real account login successful
      const companyUser: CompanyUser = {
        id: data.id,
        name: data.contact_name,
        email: data.email,
        companyName: data.name,
        contactName: data.contact_name,
        contactTitle: data.contact_title,
        phone: data.contact_phone,
        location: data.location,
        industry: data.industry,
        isDemo: false,
      };
      
      // Set user state FIRST
      setUser(companyUser);
      
      // Then store in storage
      if (rememberMe) {
        localStorage.setItem('company_session', JSON.stringify(companyUser));
        console.log('Saved to localStorage');
      } else {
        sessionStorage.setItem('company_session', JSON.stringify(companyUser));
        console.log('Saved to sessionStorage');
      }
      
      // Small delay to ensure state is updated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const logout = () => {
    console.log('Logging out');
    setUser(null);
    localStorage.removeItem('company_session');
    sessionStorage.removeItem('company_session');
  };

  const getRememberedEmail = () => {
    return localStorage.getItem(REMEMBERED_EMAIL_KEY);
  };

  const saveRememberedEmail = (email: string) => {
    localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
  };

  const clearRememberedEmail = () => {
    localStorage.removeItem(REMEMBERED_EMAIL_KEY);
  };

  return (
    <CompanyAuthContext.Provider value={{ 
      user, 
      isAuthenticated: !!user, 
      isLoading, 
      login, 
      logout,
      getRememberedEmail,
      saveRememberedEmail,
      clearRememberedEmail
    }}>
      {children}
    </CompanyAuthContext.Provider>
  );
}

export function useCompanyAuth() {
  const context = useContext(CompanyAuthContext);
  if (!context) throw new Error('useCompanyAuth must be used within CompanyAuthProvider');
  return context;
}